import { motion } from "framer-motion";
import {
  Compass,
  CalendarDays,
  Ticket,
  BellRing,
  Timer,
  BarChart3,
  HardHat,
} from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const CROWD_IMG =
  "https://images.unsplash.com/photo-1773346563165-f63acbfd06a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxjb25jZXJ0JTIwY3Jvd2QlMjBuZW9uJTIwbGlnaHRzJTIwbmlnaHRsaWZlJTIwdmVudWUlMjBzdGFnZSUyMHBlcmZvcm1hbmNlfGVufDB8fHx8MTc5MDYyOTc0NXww&ixlib=rb-4.1.0&q=85";

const ease = [0.16, 1, 0.3, 1];

const card = (slug) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease },
  "data-testid": `bento-card-${slug}`,
});

const ProductTag = ({ children }) => (
  <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.25em] text-steel/70">{children}</span>
);

const CardShell = ({ slug, icon: Icon, title, desc, tag, className = "" }) => (
  <motion.div {...card(slug)} className={`group relative overflow-hidden border border-line bg-white transition-all duration-300 hover:border-accent/50 hover:shadow-[0_8px_40px_rgba(124,58,237,0.08)] ${className}`}>
    <div className="relative z-10 flex h-full flex-col p-7 sm:p-8">
      <div className="mb-5 flex items-start justify-between">
        <span className="inline-flex h-10 w-10 items-center justify-center border border-line bg-surface text-charcoal-900 transition-colors duration-300 group-hover:border-accent group-hover:bg-accent group-hover:text-white">
          <Icon className="h-5 w-5" strokeWidth={1.8} />
        </span>
        <ProductTag>{tag}</ProductTag>
      </div>
      <h3 className="font-display text-lg font-bold tracking-tight text-charcoal-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-steel">{desc}</p>
    </div>
  </motion.div>
);

const Bento = () => (
  <section id="product" data-testid="product-section" className="bg-surface py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead
        eyebrow="Inside the Products"
        title="Every feature ships inside a product."
        lead="LANTIX covers the fan side, from discovery to the door. LANTIX PRO covers the crew side, from gig listings to promoter analytics."
      />

      <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:auto-rows-[236px] lg:grid-cols-12">
        <motion.div {...card("crew-gigs")} className="group relative overflow-hidden border border-line sm:col-span-2 lg:col-span-8 lg:row-span-2">
          <img
            src={CROWD_IMG}
            alt="Live event stage and crowd"
            className="treated absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900 via-charcoal-900/50 to-transparent" />
          <div className="relative z-10 flex h-full flex-col justify-end p-7 sm:p-9">
            <span className="mb-4 inline-flex w-fit items-center gap-2 border border-white/20 bg-charcoal-900/70 px-3.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
              <HardHat className="h-3.5 w-3.5 text-violet-400" /> LANTIX PRO · Signature Feature
            </span>
            <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">Crew &amp; Production Gigs</h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">
              Job opportunities for event crew, stagehands, and production companies, with gig
              listings that keep calendars full when work is down.
            </p>
          </div>
        </motion.div>

        <CardShell slug="event-pages" icon={CalendarDays} title="Event Pages" desc="Immersive rich-media venue previews and artist lineup spotlights." tag="LANTIX" className="lg:col-span-4" />
        <CardShell slug="ticketing-system" icon={Ticket} title="Ticketing System" desc="Zero-friction mobile checkout with instant QR entry." tag="LANTIX" className="lg:col-span-4" />
        <CardShell slug="discovery-feed" icon={Compass} title="Discovery Feed" desc="A video-first feed of tonight's best events, tuned to your taste and your city." tag="LANTIX" className="lg:col-span-4" />
        <CardShell slug="drop-alerts" icon={BellRing} title="Drop Alerts" desc="Real-time push notifications for surprise drops and secret sets." tag="LANTIX" className="lg:col-span-4" />
        <CardShell slug="last-minute-tickets" icon={Timer} title="Last-Minute Tickets" desc="Dynamic pricing fills unsold inventory in the final hours." tag="LANTIX" className="lg:col-span-4" />

        <motion.div {...card("promoter-dashboard")} className="group border border-line bg-white p-7 transition-all duration-300 hover:border-accent/50 hover:shadow-[0_8px_40px_rgba(124,58,237,0.08)] sm:p-8 lg:col-span-12">
          <div className="flex h-full flex-col items-start justify-between gap-5 md:flex-row md:items-center">
            <div className="flex items-center gap-5">
              <span className="inline-flex h-10 w-10 items-center justify-center border border-line bg-surface text-charcoal-900 transition-colors duration-300 group-hover:border-accent group-hover:bg-accent group-hover:text-white">
                <BarChart3 className="h-5 w-5" strokeWidth={1.8} />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold tracking-tight text-charcoal-900">Promoter Dashboard</h3>
                <ProductTag>LANTIX PRO</ProductTag>
              </div>
            </div>
            <p className="max-w-2xl text-sm leading-relaxed text-steel md:text-right">
              Self-service event uploads, real-time analytics, and audience insights for
              independent promoters and production companies.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

export default Bento;
